
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Icons } from '@/components/ui/icons';
import { PersonalInformationCard } from './details/PersonalInformationCard';
import { AdditionalInformationCard } from './details/AdditionalInformationCard';
import { ClientQuestionnaireSummary } from './details/ClientQuestionnaireSummary';
import { useClientDetails } from './details/useClientDetails';

interface ClientDetailsContentProps {
  userId: string;
  refreshUserProfile: () => void;
  initialData?: {
    email?: string;
    phone?: string;
    address?: string;
    emergencyContact?: string;
    birthDate?: string;
    clothingSize?: string;
    notes?: string;
  };
}

export function ClientDetailsContent({ 
  userId, 
  refreshUserProfile, 
  initialData = {} 
}: ClientDetailsContentProps) {
  const { t } = useTranslation(["clients", "common"]);
  const {
    formData,
    isSubmitting,
    isUploading,
    isLoading,
    userProfile,
    loadError,
    retryLoad,
    handleChange,
    handleDateChange,
    handleFileUpload,
    handleSubmit,
    setFormData,
    handleSwitchChange
  } = useClientDetails(userId, refreshUserProfile, initialData);

  // Debug loading state properly
  useEffect(() => {
    console.log("ClientDetailsContent - isLoading state:", isLoading);
    
    if (isLoading) {
      console.log("ClientDetailsContent - Rendering loading state");
    } else {
      console.log("ClientDetailsContent - Rendering form content");
    }
  }, [isLoading]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Icons.spinner className="h-6 w-6 animate-spin" />
        <span className="ml-2">{t('loading', { ns: 'common' })}</span>
      </div>
    );
  }
  
  if (loadError) {
    return <div role="alert" className="space-y-3 py-6">
      <p className="text-sm text-destructive">{t('questionnaire.loadError', { ns: 'clients' })}</p>
      <Button variant="outline" onClick={retryLoad}>{t('questionnaire.retry', { ns: 'clients' })}</Button>
    </div>;
  }

  // The questionnaire uses committed server notes, never an unsaved draft.
  return (
    <div className="min-w-0 space-y-6">
      <ClientQuestionnaireSummary notes={userProfile?.notes} identity={{ firstName: userProfile?.first_name, lastName: userProfile?.last_name, phone: userProfile?.phone, email: initialData.email }} />
      <form onSubmit={handleSubmit} className="grid min-w-0 gap-4">
      <PersonalInformationCard 
        formData={formData} 
        handleChange={handleChange}
        handleDateChange={handleDateChange}
        setFormData={setFormData}
      />
      
      <AdditionalInformationCard 
        formData={formData} 
        handleChange={handleChange}
        handleSwitchChange={handleSwitchChange}
      />
      
      <Button type="submit" disabled={isSubmitting}>
        {isSubmitting ? (
          <>
            <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            {t('updating', { ns: 'common', defaultValue: 'Updating' })}...
          </>
        ) : (
          t('update', { ns: 'common', defaultValue: 'Update' })
        )}
      </Button>
      </form>
    </div>
  );
}
